import React from "react";
import { Container, Typography, Box, Card, Chip } from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import GroupsIcon from "@mui/icons-material/Groups";
import ScienceIcon from "@mui/icons-material/Science";
import SectionHeading from "./SectionHeading";
import useReveal from "./useReveal";
import { brand, gradients } from "../theme";

const milestones = [
  {
    year: "2026",
    icon: <SmartToyIcon />,
    title: "AI Academy Launch",
    desc: "Dedicated campus training centers focused on MLOps, foundational transformer models, and generative AI configurations.",
  },
  {
    year: "2027",
    icon: <AutoAwesomeIcon />,
    title: "Enterprise AI Solutions",
    desc: "A commercial suite of cognitive software tools to automate complex corporate processes and run predictive analytics.",
  },
  {
    year: "2028",
    icon: <CloudQueueIcon />,
    title: "SaaS Products Scaling",
    desc: "Expanding Zealous Profoundry's cloud infrastructure into international higher-education networks for remote programs.",
  },
  {
    year: "2029",
    icon: <GroupsIcon />,
    title: "Multi-Institution Partnerships",
    desc: "Shared regional Innovation Labs facilitating collaborative data projects and joint software prototyping.",
  },
  {
    year: "2030",
    icon: <ScienceIcon />,
    title: "Research Collaborations",
    desc: "Dedicated research wings on the convergence of automated cloud computing and edge-based industrial IoT.",
  },
];

const Roadmap = () => {
  const ref = useReveal();
  return (
    <section id="roadmap-section" className="roadmap-section" ref={ref}>
      <Box sx={{ py: { xs: 7, md: 11 }, background: gradients.navy, color: "#fff", position: "relative", overflow: "hidden" }}>
        <Box
          sx={{
            position: "absolute",
            top: -100,
            left: -80,
            width: 280,
            height: 280,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(12,131,200,0.35), transparent 70%)",
          }}
        />
        <Container sx={{ position: "relative", zIndex: 1 }}>
          <SectionHeading
            eyebrow="STRATEGIC CORPORATE ROADMAP"
            title="Our 2026–2030 Vision"
            subtitle="Long-term growth directives that keep our institutional and corporate partners ahead of deep-tech trends."
            light
          />

          <Box sx={{ position: "relative" }}>
            {/* Vertical line */}
            <Box
              sx={{
                position: "absolute",
                left: { xs: 28, md: "50%" },
                top: 0,
                bottom: 0,
                width: 2,
                bgcolor: "rgba(255,255,255,0.15)",
                transform: { md: "translateX(-50%)" },
              }}
            />

            {milestones.map((m, i) => {
              const leftSide = i % 2 === 0;
              return (
                <Box
                  key={m.year}
                  className="reveal"
                  sx={{
                    position: "relative",
                    display: "flex",
                    justifyContent: { xs: "flex-start", md: leftSide ? "flex-start" : "flex-end" },
                    mb: 4,
                    pl: { xs: 8, md: 0 },
                  }}
                >
                  {/* Node */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: { xs: 28, md: "50%" },
                      top: 18,
                      ml: "-18px",
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: gradients.brand,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "3px solid #0d2438",
                      zIndex: 2,
                      "& svg": { fontSize: 18 },
                    }}
                  >
                    {m.icon}
                  </Box>

                  <Card
                    elevation={0}
                    sx={{
                      width: { xs: "100%", md: "44%" },
                      p: 3,
                      bgcolor: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      backdropFilter: "blur(6px)",
                      transition: "transform .3s ease, background .3s ease",
                      "&:hover": { transform: "translateY(-4px)", bgcolor: "rgba(255,255,255,0.10)" },
                    }}
                  >
                    <Chip
                      label={m.year}
                      size="small"
                      sx={{ bgcolor: brand.orange, color: "#fff", fontWeight: 800, mb: 1.5 }}
                    />
                    <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, mb: 1 }}>
                      {m.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.82)" }}>
                      {m.desc}
                    </Typography>
                  </Card>
                </Box>
              );
            })}
          </Box>

          <Box
            className="reveal"
            sx={{
              mt: 4,
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              textAlign: "center",
              border: "1px solid rgba(252,122,70,0.4)",
              background: "rgba(252,122,70,0.10)",
            }}
          >
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
              By 2030 — serving <Box component="span" sx={{ color: brand.orangeLight }}>200+ partner campuses</Box>{" "}
              while delivering premium custom software solutions worldwide.
            </Typography>
          </Box>
        </Container>
      </Box>
    </section>
  );
};

export default Roadmap;
